import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Divider
} from '@mui/material';

const SurveySection = ({ email, onSurveySubmitted }) => {
  const [source, setSource] = useState('');
  const [autreDetail, setAutreDetail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [checking, setChecking] = useState(!!email);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Vérifier si cet email a déjà répondu au sondage → si oui, on cache tout
  useEffect(() => {
    if (!email) {
      setChecking(false);
      return;
    }
    let active = true;
    fetch(`${API_URL}/surveys/check/${encodeURIComponent(email)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data?.completed) setAlreadyCompleted(true);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, [email, API_URL]);

  const handleSubmit = async () => {
    if (!source) {
      setError('Veuillez sélectionner une option');
      return;
    }

    if (source === 'autre' && !autreDetail.trim()) {
      setError('Veuillez préciser votre réponse');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/surveys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          source,
          autre_detail: source === 'autre' ? autreDetail : null
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la soumission');
      }

      setSubmitted(true);
      if (onSurveySubmitted) {
        onSurveySubmitted();
      }
    } catch (err) {
      setError('Erreur lors de la soumission du sondage');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Pendant la vérification, ou si la personne a déjà répondu : on n'affiche rien
  if (checking || alreadyCompleted) {
    return null;
  }

  // Affichage après soumission réussie
  if (submitted) {
    return (
      <Card sx={{ 
        backgroundColor: '#e8f5e9', 
        border: '1px solid #4caf50',
        mb: 3
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
              ✓ Merci pour vos informations!
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#2e7d32', mt: 1 }}>
            Vos réponses nous aident à améliorer nos services.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3, backgroundColor: '#f9f9f9' }}>
      <CardContent>
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          color: '#c62828',
          mb: 1
        }}>
          📋 Comment avez-vous entendu parler de nous? (Optionnel)
        </Typography>
        
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Votre réponse nous aide à améliorer notre communication.
        </Typography>

        <Divider sx={{ my: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormControl fullWidth>
          <RadioGroup
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setError('');
            }}
          >
            <FormControlLabel
              value="facebook"
              control={<Radio />}
              label="Facebook"
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              value="entourage"
              control={<Radio />}
              label="Entourage (ami, famille, collègue)"
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              value="croix_rouge"
              control={<Radio />}
              label="Site de la Croix-Rouge"
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              value="google"
              control={<Radio />}
              label="Google"
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              value="autre"
              control={<Radio />}
              label="Autre"
              sx={{ mb: 1 }}
            />
          </RadioGroup>
        </FormControl>

        {source === 'autre' && (
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Veuillez préciser..."
            value={autreDetail}
            onChange={(e) => setAutreDetail(e.target.value)}
            sx={{ mt: 2 }}
            variant="outlined"
            size="small"
          />
        )}

        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mt: 3,
          justifyContent: 'flex-end'
        }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            disabled={loading || !source}
            sx={{ minWidth: 150 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Soumettre le sondage'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SurveySection;
